import React from 'react';
import {
  ArrowClockwise,
  ClockCountdown,
  Copy,
  LightbulbFilament,
  PushPin,
  PushPinSlash,
  Stack,
  StackMinus,
  StackPlus,
  Stop,
  // Nav rail / header / menus (Console shell)
  Chat,
  ChartLine,
  Database,
  Folders,
  Lightning,
  ClockCounterClockwise,
  GearSix,
  Lock,
  BellSimple,
  EnvelopeSimple,
  EnvelopeSimpleOpen,
  Question,
  UserCircle,
  ArrowsLeftRight,
  SignOut,
  DotsThreeVertical,
  LinkBreak,
  Prohibit,
  Users,
  UserPlus,
  UserMinus,
  // General set (targets of the Material -> Phosphor migration map)
  Plus,
  PlusCircle,
  ArrowLeft,
  ArrowDown,
  ArrowRight,
  ArrowUp,
  ArrowsDownUp,
  CaretDown,
  ClipboardText,
  ListChecks,
  Paperclip,
  XCircle,
  ChatCircle,
  Check,
  CheckCircle,
  X,
  Cloud,
  CloudArrowUp,
  Moon,
  SquaresFour,
  FileText,
  DownloadSimple,
  File,
  DotsSix,
  NotePencil,
  WarningCircle,
  Flag,
  FolderOpen,
  ChatsCircle,
  FastForward,
  Waveform,
  WaveformSlash,
  GridFour,
  Info,
  Link,
  Cpu,
  BookOpen,
  Microphone,
  MicrophoneSlash,
  Bell,
  ArrowSquareOut,
  User,
  FilePdf,
  Play,
  Rewind,
  Clock,
  TreeStructure,
  Gear,
  CellSignalHigh,
  Note,
  StopCircle,
  ArrowsClockwise,
  Archive,
  Table,
  UploadSimple,
  FileArrowUp,
  List,
  SpeakerHigh,
  Warning,
  WifiHigh,
  MagnifyingGlass,
  Trash,
  TrashSimple,
  PencilSimple,
  Circle,
  // PRD-1413 icon sweep — targets of the broader Material migration.
  Sparkle,
  BookmarkSimple,
  Buildings,
  CaretLeft,
  Scissors,
  PuzzlePiece,
  Folder,
  Function as FunctionIcon,
  UsersThree,
  Hourglass,
  HourglassHigh,
  Image as ImageIcon,
  Camera,
  Brain,
  ChatText,
  FloppyDisk,
  MagnifyingGlassMinus,
  Shield,
  ToggleLeft,
  EyeSlash,
  Pause,
  // Assessment detail: the Unclear outcome's indeterminate dash.
  Minus,
  // Dynamic-name targets (chevrons, visibility, chart types in Insights widgets).
  CaretUp,
  CaretRight,
  Eye,
  ListDashes,
  FirstAidKit,
  NumberCircleOne,
  ChartBar,
  ChartScatter,
  ChartPie,
  ChartDonut,
  Equalizer,
  SlidersHorizontal,
} from '@phosphor-icons/react';

// ---------------------------------------------------------------------------
// Phosphor name -> component. Keyed by the Phosphor kebab name so call sites can
// write Phosphor names directly (e.g. <Icon name="chart-line" />).
// ---------------------------------------------------------------------------
const PHOSPHOR = {
  // Ask ResAI Iteration 1 (PRD-1621..1624)
  'arrow-clockwise': ArrowClockwise,
  'clock-countdown': ClockCountdown,
  copy: Copy,
  'lightbulb-filament': LightbulbFilament,
  'push-pin': PushPin,
  'push-pin-slash': PushPinSlash,
  stack: Stack,
  'stack-minus': StackMinus,
  'stack-plus': StackPlus,
  stop: Stop,
  chat: Chat,
  'chart-line': ChartLine,
  database: Database,
  folders: Folders,
  lightning: Lightning,
  'clock-counter-clockwise': ClockCounterClockwise,
  'gear-six': GearSix,
  lock: Lock,
  'bell-simple': BellSimple,
  'envelope-simple': EnvelopeSimple,
  'envelope-simple-open': EnvelopeSimpleOpen,
  question: Question,
  'user-circle': UserCircle,
  'arrows-left-right': ArrowsLeftRight,
  'sign-out': SignOut,
  'dots-three-vertical': DotsThreeVertical,
  plus: Plus,
  'plus-circle': PlusCircle,
  'arrow-left': ArrowLeft,
  'arrow-right': ArrowRight,
  'arrow-up': ArrowUp,
  'arrow-down': ArrowDown,
  'arrows-down-up': ArrowsDownUp, // PRD-1700: List table's unsorted header
  'caret-down': CaretDown,
  'clipboard-text': ClipboardText,
  'list-checks': ListChecks,
  paperclip: Paperclip,
  'x-circle': XCircle,
  'chat-circle': ChatCircle,
  check: Check,
  'check-circle': CheckCircle,
  x: X,
  cloud: Cloud,
  'cloud-arrow-up': CloudArrowUp,
  moon: Moon,
  'squares-four': SquaresFour,
  'file-text': FileText,
  'download-simple': DownloadSimple,
  file: File,
  'dots-six': DotsSix,
  'note-pencil': NotePencil,
  'warning-circle': WarningCircle,
  flag: Flag,
  'folder-open': FolderOpen,
  'chats-circle': ChatsCircle,
  'fast-forward': FastForward,
  waveform: Waveform,
  // PRD-1703: an assessment built on a transcript has no recording.
  'waveform-slash': WaveformSlash,
  'grid-four': GridFour,
  info: Info,
  link: Link,
  'link-break': LinkBreak,
  prohibit: Prohibit,
  users: Users,
  'user-plus': UserPlus,
  'user-minus': UserMinus,
  cpu: Cpu,
  'book-open': BookOpen,
  microphone: Microphone,
  'microphone-slash': MicrophoneSlash,
  bell: Bell,
  'arrow-square-out': ArrowSquareOut,
  user: User,
  'file-pdf': FilePdf,
  play: Play,
  minus: Minus,
  rewind: Rewind,
  clock: Clock,
  'tree-structure': TreeStructure,
  gear: Gear,
  'cell-signal-high': CellSignalHigh,
  note: Note,
  'stop-circle': StopCircle,
  'arrows-clockwise': ArrowsClockwise,
  // PRD-1607: Library archive / unarchive (no existing glyph covered it).
  archive: Archive,
  table: Table,
  'upload-simple': UploadSimple,
  'file-arrow-up': FileArrowUp,
  list: List,
  'speaker-high': SpeakerHigh,
  warning: Warning,
  'wifi-high': WifiHigh,
  'magnifying-glass': MagnifyingGlass,
  trash: Trash,
  'trash-simple': TrashSimple,
  'pencil-simple': PencilSimple,
  circle: Circle,
  sparkle: Sparkle,
  'bookmark-simple': BookmarkSimple,
  buildings: Buildings,
  'caret-left': CaretLeft,
  scissors: Scissors,
  'puzzle-piece': PuzzlePiece,
  folder: Folder,
  function: FunctionIcon,
  'users-three': UsersThree,
  hourglass: Hourglass,
  'hourglass-high': HourglassHigh,
  image: ImageIcon,
  camera: Camera,
  brain: Brain,
  'chat-text': ChatText,
  'floppy-disk': FloppyDisk,
  'magnifying-glass-minus': MagnifyingGlassMinus,
  shield: Shield,
  'toggle-left': ToggleLeft,
  'eye-slash': EyeSlash,
  pause: Pause,
  'caret-up': CaretUp,
  'caret-right': CaretRight,
  eye: Eye,
  'list-dashes': ListDashes,
  'first-aid-kit': FirstAidKit,
  'number-circle-one': NumberCircleOne,
  'chart-bar': ChartBar,
  'chart-scatter': ChartScatter,
  'chart-pie': ChartPie,
  'chart-donut': ChartDonut,
  equalizer: Equalizer,
  'sliders-horizontal': SlidersHorizontal,
};

// ---------------------------------------------------------------------------
// Legacy Material icon name -> Phosphor name. Lets call sites that still pass a
// Material name keep working during the migration (PRD-1413); an unmapped name
// passes straight through, so new code writes Phosphor names and this map only
// ever shrinks. Lifted from the icon-migration handoff.
// ---------------------------------------------------------------------------
export const MATERIAL_TO_PHOSPHOR = {
  add: 'plus',
  add_circle: 'plus-circle',
  add_circle_outline: 'plus-circle',
  arrow_back: 'arrow-left',
  auto_awesome: 'sparkle',
  bookmark_border: 'bookmark-simple',
  business: 'buildings',
  chevron_left: 'caret-left',
  chevron_right: 'caret-right',
  content_cut: 'scissors',
  delete: 'trash',
  delete_outline: 'trash',
  edit: 'pencil-simple',
  error_outline: 'warning-circle',
  expand_less: 'caret-up',
  extension_off: 'puzzle-piece',
  visibility: 'eye',
  list_alt: 'list-dashes',
  medical_information: 'first-aid-kit',
  looks_one: 'number-circle-one',
  show_chart: 'chart-line',
  mediation: 'tree-structure',
  bar_chart: 'chart-bar',
  scatter_plot: 'chart-scatter',
  stacked_bar_chart: 'chart-bar',
  area_chart: 'chart-line',
  equalizer: 'equalizer',
  pie_chart: 'chart-pie',
  donut_large: 'chart-donut',
  folder: 'folder',
  functions: 'function',
  groups: 'users-three',
  hourglass_empty: 'hourglass',
  hourglass_top: 'hourglass-high',
  image: 'image',
  info_outline: 'info',
  more_vert: 'dots-three-vertical',
  pause: 'pause',
  photo_camera: 'camera',
  psychology: 'brain',
  rate_review: 'chat-text',
  refresh: 'arrows-clockwise',
  restore: 'clock-counter-clockwise',
  rule: 'list-checks',
  save: 'floppy-disk',
  search: 'magnifying-glass',
  search_off: 'magnifying-glass-minus',
  shield: 'shield',
  toggle_off: 'toggle-left',
  visibility_off: 'eye-slash',
  link_off: 'link-break',
  block: 'prohibit',
  group: 'users',
  warning_amber: 'warning',
  arrow_drop_down: 'caret-down',
  arrow_forward: 'arrow-right',
  arrow_upward: 'arrow-up',
  assignment: 'clipboard-text',
  assignment_turned_in: 'list-checks',
  attach_file: 'paperclip',
  cancel: 'x-circle',
  chat_bubble: 'chat-circle',
  chat_bubble_outline: 'chat-circle',
  account_tree: 'tree-structure',
  check: 'check',
  check_circle: 'check-circle',
  check_circle_outline: 'check-circle',
  checklist: 'list-checks',
  close: 'x',
  cloud: 'cloud',
  cloud_upload: 'cloud-arrow-up',
  dark_mode: 'moon',
  dashboard: 'squares-four',
  database: 'database',
  description: 'file-text',
  download: 'download-simple',
  draft: 'file',
  drag_handle: 'dots-six',
  edit_note: 'note-pencil',
  error: 'warning-circle',
  expand_more: 'caret-down',
  fact_check: 'clipboard-text',
  file_download: 'download-simple',
  tune: 'sliders-horizontal',
  flag: 'flag',
  folder_copy: 'folders',
  folder_open: 'folder-open',
  forum: 'chats-circle',
  forward_10: 'fast-forward',
  graphic_eq: 'waveform',
  grid_view: 'grid-four',
  history: 'clock-counter-clockwise',
  info: 'info',
  insights: 'chart-line',
  link: 'link',
  lock: 'lock',
  lock_outline: 'lock',
  memory: 'cpu',
  menu_book: 'book-open',
  mic: 'microphone',
  mic_off: 'microphone-slash',
  notifications: 'bell',
  open_in_new: 'arrow-square-out',
  person: 'user',
  picture_as_pdf: 'file-pdf',
  play_arrow: 'play',
  replay_10: 'rewind',
  schedule: 'clock',
  schema: 'tree-structure',
  settings: 'gear',
  signal_cellular_alt: 'cell-signal-high',
  sticky_note_2: 'note',
  stop_circle: 'stop-circle',
  sync: 'arrows-clockwise',
  table_chart: 'table',
  upload: 'upload-simple',
  upload_file: 'file-arrow-up',
  view_list: 'list',
  volume_up: 'speaker-high',
  warning: 'warning',
  wifi: 'wifi-high',
};

/**
 * Icon — the single Phosphor 2.1 icon component for the Console (PRD-1412).
 *
 * Weight follows size: 16px and under is Bold, larger is Regular (the ResUI
 * icon rule); override with the `weight` prop for the documented exceptions.
 * `name` accepts a Phosphor name (e.g. "chart-line") or a legacy Material name
 * (e.g. "insights"), which is translated via MATERIAL_TO_PHOSPHOR.
 *
 * Icons are decorative (`aria-hidden`); the surrounding control carries the
 * accessible name. Colour comes from `currentColor`, so set it on a parent.
 */
function Icon({ name, size = 24, weight, className, title, ...rest }) {
  const resolved = MATERIAL_TO_PHOSPHOR[name] || name;
  const Glyph = PHOSPHOR[resolved];
  // Phosphor is now the only way icons are drawn (the Material font is gone), so an
  // unmapped name is a real gap, not a cosmetic one. Fall back to a visible circle in
  // production (a wrong glyph beats a crash) but announce the miss in dev/test so the
  // next gap surfaces the first time it renders, not when someone squints at the screen.
  if (!Glyph && process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.warn(`[Icon] No Phosphor glyph for name="${name}" (resolved "${resolved}") — rendering fallback circle.`);
  }
  const Final = Glyph || Circle;
  const resolvedWeight = weight || (size <= 16 ? 'bold' : 'regular');
  return (
    <Final
      size={size}
      weight={resolvedWeight}
      className={className}
      // Stable hook for tests/debugging: the requested name (Material alias or Phosphor).
      data-icon={name}
      aria-hidden={title ? undefined : true}
      aria-label={title}
      {...rest}
    />
  );
}

export default Icon;
